import styled from '@emotion/styled'
import { ReactNode, useState } from 'react'

import { Profession, Specialization } from '@bemlo/enums'
import { ShiftTimes } from '@bemlo/schedule'

import { OverviewCalendarRow } from './overview-calendar/OverviewCalendarRow'
import { OverviewCalendarTenderTitle } from './overview-calendar/OverviewCalendarTenderTitle'
import { useGetHoursPerWeek } from './overview-calendar/useGetHoursPerWeek'
import { Color, theme } from '../../constants'
import { Clickable, Flex, Icon, Text } from '../../elements'

type Tender = {
  id: string
  title: string
  profession: Profession
  specializations: Specialization[]
  startDate: number
  endDate: number
  vacantShifts: ShiftTimes[]
}

type Unit = {
  id: string
  name: string
  tenders: Tender[]
}

const UnitRowTitle = styled(Clickable)({
  display: 'flex',
  gap: theme.spacing['1.5'],
  padding: `${theme.spacing[1]}px ${theme.spacing[2]}px`,
  width: '100%',
})

type TendersOverviewCalendarProps = {
  startDate: number
  endDate: number
  units: Unit[]
  controls?: ReactNode
  onClickTender: (id: string) => void
}

const RowsWrapper = styled('div')({
  flexGrow: 1,
  flexBasis: 0,
  overflow: 'auto',
})

export const TendersOverviewCalendar = ({
  startDate,
  endDate,
  units,
  controls = null,
  onClickTender,
}: TendersOverviewCalendarProps) => {
  const [openUnits, setOpenUnits] = useState<Record<string, boolean>>({})
  const [weeks, getHoursPerWeek] = useGetHoursPerWeek(startDate, endDate)

  return (
    <Flex
      col
      styles={{ width: '100%', height: '100%', background: Color.WHITE }}
    >
      <OverviewCalendarRow
        type="HEADER"
        title={controls}
        weeks={weeks.map((week) => week.number)}
      />
      <RowsWrapper>
        {units.flatMap((unit) => {
          const isOpen = !!openUnits[unit.id]
          const weeklyHoursPerTender = unit.tenders.map((tender) =>
            getHoursPerWeek(tender.vacantShifts, tender),
          )
          const weeklyHourForUnit = weeklyHoursPerTender.reduce((acc, curr) => {
            curr.forEach((week, index) => {
              acc[index] = (acc[index] || 0) + (week ?? 0)
            })
            return acc
          }, [] as number[])

          return (
            <>
              <OverviewCalendarRow
                type="UNIT"
                title={
                  <UnitRowTitle
                    onClick={() =>
                      setOpenUnits((curr) => ({ ...curr, [unit.id]: !isOpen }))
                    }
                  >
                    <Icon
                      name={isOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'}
                    />
                    <Text size={4} bold>
                      {unit.name}
                    </Text>
                  </UnitRowTitle>
                }
                weeks={weeklyHourForUnit}
              />
              {isOpen &&
                unit.tenders.map((tender, i) => (
                  <OverviewCalendarRow
                    isNested
                    key={tender.id}
                    type="TENDER"
                    title={
                      <OverviewCalendarTenderTitle
                        onClick={onClickTender}
                        {...tender}
                      />
                    }
                    weeks={weeklyHoursPerTender[i]}
                  />
                ))}
            </>
          )
        })}
      </RowsWrapper>
    </Flex>
  )
}
